import React, { useEffect, useMemo, useState } from "react";
import "./ProjectDetails.scss";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";

import { ReactComponent as InviteIcon } from "src/images/share-plus.svg";
import { AppLocation } from "src/redux/main/mainReducer";
import { RootState } from "src/redux/reducers";
import {
  Breadcrumbs,
  BreadcrumbsItem,
} from "../components/Breadcrumbs/Breadcrumbs";
import TaskMenuDropdown from "../dropdowns/TaskMenuDropdown/TaskMenuDropdown";

import TaskMetaFields from "../Task/components/TaskMetaFields/TaskMetaFields";
import { tableDataType } from "../components/Table/Table";
import { TabsContainer } from "./TabsContainer/TabsContainer";
import { Button } from "../components/Button/Button";
import AvatarPlaceholder from "../../images/avatar-purple.svg";
import {
  PaneTopbar,
  PaneTopbarLeft,
  PaneTopbarToolbar,
} from "../components/Pane/PaneTopbar/PaneTopbar";
import ProjectCoverImage from "../components/ProjectCoverImage/ProjectCoverImage";
import ProjectInfo from "../components/ProjectInfo/ProjectInfo";
import {
  clearProjectDetails,
  getProject,
  getProjectDetails,
} from "../../redux";
import { copyLink, getImageSource } from "../../utils/methods";
import RenameModal from "../modals/RenameModal/RenameModal";
import { SeeAsClientInfoModal } from "../modals/ShareModal/SeeAsClientInfoModal/SeeAsClientInfoModal";
import { RolesInfoModal } from "../modals/ShareModal/RolesInfoModal/RolesInfoModal";
import IDHFormattedMessage from "../components/IDHFormattedMessage/IDHFormattedMessage";
import { SettingsModal } from "../SettingsModal/SettingsModal";
import { SettingsContext, SettingsTab } from "../SettingsModal/enums";
import { openSettingsModal } from "../SettingsModal/methods";
import { AddedBy } from "../Task/components/AddedBy/AddedBy";
import { AppConfig } from "../../config/AppConfig";
import { Metadata } from "../Task/Subtask/Subtask";
import { ProjectPermissionsEnum } from "../../utils/PermissionsEnums";
import { Pane } from "../components/Pane/Pane";

interface ProjectDetailsProps {
  searchParams: string;
}

function ProjectDetails({ searchParams }: ProjectDetailsProps) {
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showSeeAsClientInfoModal, setShowSeeAsClientInfoModal] =
    useState(false);
  const [showRulesInfoModal, setShowRolesInfoModal] = useState(false);

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const { projectId }: { projectId: string } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    projectReducer: { projectDetails, projectBasicData, membersList },
    mainReducer: {
      settingsModalData: { location },
    },
  } = useSelector((state: RootState) => state);

  const projectColumnRanks = useSelector(
    (state: RootState) => state.projectFiltersReducer.projectColumnRanks,
  );

  const hiddenColumns = useSelector(
    (state: RootState) => state.projectFiltersReducer.hiddenColumns,
  );

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    dispatch(getProjectDetails(projectId));
  }, [projectId]);

  useEffect(() => {
    return () => {
      dispatch(clearProjectDetails());
    };
  }, []);

  const filteredMetadata = useMemo(
    () =>
      (projectDetails?.metadata
        ?.filter(
          (el) =>
            el.taskType === null &&
            el.name !== "project_name" &&
            el.name !== "owner",
        )
        .sort((a, b) => {
          const rankA = projectColumnRanks?.find(
            (col: { uuid: string }) => col.uuid === a.uuid,
          )?.rank;
          const rankB = projectColumnRanks?.find(
            (col: { uuid: string }) => col.uuid === b.uuid,
          )?.rank;
          if (rankA && rankB) return rankA.localeCompare(rankB);
          return 0;
        }) as unknown as Metadata[]) || [],
    [projectDetails?.metadata, projectColumnRanks, hiddenColumns],
  );

  if (projectDetails === null || projectBasicData === null) {
    return null;
  }

  const projectOwner = membersList.find(
    (member) => member.id === projectDetails.ownerWsMemberUuid,
  );

  const projectNameMetaField = projectDetails.metadata?.find(
    (el) => el.key === "project_name",
  );

  const openShareModal = () => {
    openSettingsModal(
      history,
      AppLocation.ProjectDetails,
      SettingsContext.Project,
      SettingsTab.Members,
    );
  };

  const handleClose = () => {
    history.push({
      pathname: `/workspace/${activeWorkspaceUuid}/projects/${projectId}`,
      search: searchParams,
    });
    setIsMounted(false);
  };

  return (
    <Pane
      className="project-details-pane"
      backdropClassName="project-details-pane-backdrop"
      hidePane={handleClose}
      showPane={isMounted}
    >
      <div className="project-details">
        <PaneTopbar>
          <PaneTopbarLeft>
            <Breadcrumbs>
              <BreadcrumbsItem
                text={
                  <IDHFormattedMessage
                    id="ws_campaigns"
                    defaultMessage="Campaigns"
                  />
                }
                to={`/workspace/${activeWorkspaceUuid}/projects/${projectDetails.projectId}`}
              />
            </Breadcrumbs>
          </PaneTopbarLeft>
          <PaneTopbarToolbar hidePane={handleClose}>
            <TaskMenuDropdown
              data={projectDetails}
              editTask={() => setShowRenameModal(true)}
              copyLink={() =>
                copyLink(
                  `${AppConfig.getAppRoute()}/${activeWorkspaceUuid}/projects/${projectDetails.projectId}`,
                )
              }
              projectId={projectId}
            />
          </PaneTopbarToolbar>
        </PaneTopbar>

        <div className="pane__container-wrap scroll-fixed">
          <div className="pane__container">
            <div className="task__person">
              <ProjectCoverImage
                src={getImageSource(projectDetails?.cover, "small")}
                updateData={() =>
                  dispatch(getProject(projectDetails.projectId))
                }
                item={projectDetails}
                parentLoading={false}
              />
              <ProjectInfo
                title={projectDetails.title}
                projectUuid={projectDetails.projectId}
                projectNameMetaField={projectNameMetaField}
              />

              {projectBasicData?.permissions?.project?.includes(
                ProjectPermissionsEnum.DISPLAY_MEMBERS_WORKSPACE_SETTINGS,
              ) && (
                <>
                  <div
                    className="projects__topbar-right-members-avatars"
                    onClick={openShareModal}
                  >
                    {projectBasicData.members.slice(0, 3).map((member) => (
                      <div className="avatar" key={member.id}>
                        <img
                          src={member.avatarUrl || AvatarPlaceholder}
                          alt="avatar"
                        />
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="white-with-grey-border"
                    size="square"
                    onClick={openShareModal}
                    className="task__btn-share"
                  >
                    <InviteIcon />
                  </Button>
                </>
              )}
            </div>

            <TaskMetaFields
              data={filteredMetadata}
              dataType={tableDataType.ProjectDetails}
              settingsContextData={{
                context: SettingsContext.Project,
                contextTargetUuid: projectDetails.projectId,
              }}
            />

            <AddedBy
              name={projectOwner?.name}
              avatarUrl={projectOwner?.avatarUrl}
              dateCreated={projectDetails.created}
            />

            <TabsContainer metadata={projectDetails.metadata} />
          </div>

          {showSeeAsClientInfoModal && (
            <SeeAsClientInfoModal
              onClose={() => setShowSeeAsClientInfoModal(false)}
              openShareModal={openShareModal}
            />
          )}

          {location === AppLocation.ProjectDetails && (
            <SettingsModal
              members={{
                openShareModal,
                openSeeAsClientInfoModal: () =>
                  setShowSeeAsClientInfoModal(true),
                openRolesInfoModal: () => setShowRolesInfoModal(true),
              }}
              contextTargetUuid={projectId}
            />
          )}

          {showSeeAsClientInfoModal && (
            <SeeAsClientInfoModal
              onClose={() => setShowSeeAsClientInfoModal(false)}
              openShareModal={openShareModal}
            />
          )}

          {showRulesInfoModal && (
            <RolesInfoModal
              onClose={() => setShowRolesInfoModal(false)}
              openShareModal={openShareModal}
            />
          )}

          {showRenameModal && (
            <RenameModal
              onClose={() => setShowRenameModal(false)}
              objectId={projectDetails.projectId}
              taskName={projectDetails.title}
              titleFieldId={
                projectDetails.metadata.find(
                  (field) => field.key === "project_name",
                )?.uuid ?? ""
              }
              projectId={projectDetails.projectId}
              dataType={tableDataType.ProjectDetails}
            />
          )}
        </div>
      </div>
    </Pane>
  );
}

export { ProjectDetails };
